import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import DemoLayout from "../components/DemoLayout";
import DemoIntroSlideshow from "../components/DemoIntroSlideshow";
import DemoStoryPanel from "../components/DemoStoryPanel";
import EinstufungToolPage from "./EinstufungToolPage";
import { DEMO_CASES } from "../demo/cases";
import {
  setActiveDemoCase,
  isDemoActive,
} from "../demo/demoInterceptor";
import { useAuth } from "../context/AuthContext";

/**
 * Iter 20.10 — DemoToolPage (Story-First-Version)
 *
 * Ablauf pro Case-Aufruf:
 *   1. `sessionStorage.et_demo_case` setzen → Interceptor + AuthContext aktiv
 *   2. `refresh()` im AuthContext → DEMO_USER wird geladen
 *   3. Intro-Slideshow zeigen (5 Slides), sofern noch nicht abgeschlossen
 *      (Marker `sessionStorage.et_demo_intro_{caseId}`)
 *   4. Nach Slideshow-Complete: Split-Screen mit `DemoStoryPanel` + `EinstufungToolPage`
 *
 * User kann jederzeit „Fall wechseln" oder „Vollversion testen" oben klicken.
 * Slideshow ist per Klick auf X überspringbar.
 */

const introMarker = (caseId) => `et_demo_intro_${caseId}`;

function hasSeenIntro(caseId) {
  try {
    return sessionStorage.getItem(introMarker(caseId)) === "1";
  } catch {
    return false;
  }
}

function markIntroSeen(caseId) {
  try {
    sessionStorage.setItem(introMarker(caseId), "1");
  } catch {
    /* ignore */
  }
}

export default function DemoToolPage() {
  const { caseId } = useParams();
  const { refresh } = useAuth();
  const [ready, setReady] = useState(false);
  const [introDone, setIntroDone] = useState(() => hasSeenIntro(caseId));

  const kase = DEMO_CASES[caseId];

  useEffect(() => {
    if (!kase) return;
    setActiveDemoCase(caseId);
    (async () => {
      await refresh();
      setReady(true);
    })();
    setTimeout(() => {
      toast.info(
        `Demo geladen: ${kase.title}. Klicken Sie sich durch — nichts wird gespeichert.`,
        { duration: 4500 }
      );
    }, 400);
  }, [caseId, kase, refresh]);

  if (!kase) {
    return <Navigate to="/demo" replace />;
  }

  if (!ready || !isDemoActive()) {
    return (
      <DemoLayout caseTitle={kase.title}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="loading-bar-brand h-[3px] w-32 mx-auto mb-4" />
            <div className="text-xs uppercase tracking-widest text-warm-500">
              Demo wird vorbereitet…
            </div>
          </div>
        </div>
      </DemoLayout>
    );
  }

  // Phase 1: Intro-Slideshow
  if (!introDone) {
    return (
      <DemoIntroSlideshow
        caseId={caseId}
        caseTitle={kase.title}
        onComplete={() => {
          markIntroSeen(caseId);
          setIntroDone(true);
        }}
      />
    );
  }

  // Phase 2: Split-Screen mit persistentem Story-Panel + Tool
  return (
    <DemoLayout caseTitle={kase.title} wide>
      <div
        className="grid grid-cols-1 lg:grid-cols-[minmax(280px,32%)_1fr] gap-6"
        data-testid="demo-split-layout"
      >
        <DemoStoryPanel caseId={caseId} />
        <div className="min-w-0">
          <EinstufungToolPage />
        </div>
      </div>
    </DemoLayout>
  );
}
